import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import handleOpenSweetAlert from '../../../components/handleOpenSweetAlert';
import { registrarBitacora } from '../../../utils/bitacoraUtils';
import { getTablaSolicitudes, verificarNivelAut, sincronizarERP } from '../../../services/solicitudesService';
import { buildFilterPayload } from '../../../utils/filterUtils';

const EMPTY_CC_ARRAY = Object.freeze([]);

const getCurrentDate = () => new Date().toLocaleDateString('en-CA');
const getSessionString = (key) => sessionStorage.getItem(key) || '';
const getSessionArray = (key) => {
  try {
    const value = sessionStorage.getItem(key);
    if (!value) return [];
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
};
const getSessionObject = (key) => {
  try {
    const value = sessionStorage.getItem(key);
    if (!value) return null;
    return JSON.parse(value);
  } catch (e) {
    return null;
  }
};

const parseMoney = (value) => {
  if (value == null) return NaN;
  if (typeof value === 'number') return Number.isFinite(value) ? value : NaN;
  const s = String(value).trim().replace(/[^\d.,-]/g, '').replace(/,/g, '');
  if (!s || s === '-' || s === '.' || s === '-.') return NaN;
  return Number(s);
};

const formatCurrency = (val, currency = 'MXN') => {
  if (val == null || !Number.isFinite(val)) return '$0.00';
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: (currency || 'MXN').toUpperCase()
  }).format(val);
};

const normalizeEmpresaArray = (idEmpresa) => {
  if (idEmpresa == null) return [];
  if (Array.isArray(idEmpresa)) {
    return idEmpresa.map((x) => Number(x)).filter((n) => Number.isFinite(n) && n > 0);
  }
  if (typeof idEmpresa === 'string') {
    const s = idEmpresa.trim();
    if (s.startsWith('[') && s.endsWith(']')) {
      try {
        const arr = JSON.parse(s);
        if (Array.isArray(arr)) return arr.map((x) => Number(x)).filter((n) => Number.isFinite(n) && n > 0);
      } catch { }
    }
    const n = Number(s);
    return Number.isFinite(n) && n > 0 ? [n] : [];
  }
  const n = Number(idEmpresa);
  return Number.isFinite(n) && n > 0 ? [n] : [];
};

const splitCentroCostoOption = (value = '') => {
  const str = String(value || '').trim();
  if (!str) return [];
  const idx = str.indexOf(' - ');
  if (idx !== -1) {
    const num = str.substring(0, idx).trim();
    const nom = str.substring(idx + 3).trim();
    return [num, nom].filter(Boolean);
  }
  return [str];
};

const getCentroCostoFilterValue = (value = '') => {
  const parts = splitCentroCostoOption(value);
  return parts.length > 1 ? parts[0] : String(value).trim();
};

const getCentroCostoFilterValues = (values = []) => {
  const result = [];
  (values || []).forEach(val => {
    const parts = splitCentroCostoOption(val);
    parts.forEach(p => {
      if (p && !result.includes(p)) result.push(p);
    });
  });
  return result;
};

const hasActiveContentFilters = (filterData = {}) => (
  Boolean(String(filterData.FOLIO || '').trim()) ||
  Boolean(String(filterData.PROVEEDOR || '').trim()) ||
  Boolean(String(filterData.EMPRESA || '').trim()) ||
  Boolean(String(filterData.PROYECTO || '').trim()) ||
  Boolean(String(filterData.DIVISION || '').trim()) ||
  Boolean(String(filterData.CENTRO_COSTO || '').trim()) ||
  Boolean(String(filterData.DEPARTAMENTO || '').trim()) ||
  Boolean(String(filterData.COMPRADOR || '').trim()) ||
  Boolean(String(filterData.REQUISITOR || '').trim()) ||
  Boolean(String(filterData.DIVISA || '').trim()) ||
  Boolean(String(filterData.ESTATUS || '').trim()) ||
  (Array.isArray(filterData.PROYECTOS) && filterData.PROYECTOS.length > 0) ||
  (Array.isArray(filterData.DIVISIONES) && filterData.DIVISIONES.length > 0) ||
  (Array.isArray(filterData.DEPARTAMENTOS) && filterData.DEPARTAMENTOS.length > 0) ||
  (Array.isArray(filterData.CENTRO_COSTOS) && filterData.CENTRO_COSTOS.length > 0) ||
  (Array.isArray(filterData.PROVEEDORES) && filterData.PROVEEDORES.length > 0) ||
  (Array.isArray(filterData.DIVISAS) && filterData.DIVISAS.length > 0) ||
  (Array.isArray(filterData.COMPRADORAS) && filterData.COMPRADORAS.length > 0)
);

const buildVisibleServerFilters = (filterData, options = {}) => {
  const payload = { ...filterData };

  delete payload.EMPRESA_IDS;
  delete payload.DEPARTAMENTOS;
  delete payload.CENTRO_COSTOS;
  delete payload.PROVEEDORES;
  delete payload.DIVISAS;
  delete payload.COMPRADORAS;
  delete payload.PROYECTOS;
  delete payload.DIVISIONES;
  delete payload.PROYECTO;
  delete payload.DIVISION;

  if (options.ignoreDateFilters) {
    payload.FECHA_INICIO = null;
    payload.FECHA_FIN = null;
  }

  payload.DEPARTAMENTO = filterData.DEPARTAMENTOS?.length ? filterData.DEPARTAMENTOS : (payload.DEPARTAMENTO || '');
  
  // Resolver centros de costo (si seleccionó CC directo, o si seleccionó Grupo o Proyecto)
  let resolvedCCs = [];
  const hasHierarchicalFilter = Boolean(
    (filterData.CENTRO_COSTOS && filterData.CENTRO_COSTOS.length > 0) ||
    (filterData.DIVISIONES && filterData.DIVISIONES.length > 0) ||
    (filterData.PROYECTOS && filterData.PROYECTOS.length > 0)
  );

  if (filterData.CENTRO_COSTOS?.length) {
    resolvedCCs = getCentroCostoFilterValues(filterData.CENTRO_COSTOS);
  } else if (options.resolvedCentroCostos?.length) {
    resolvedCCs = options.resolvedCentroCostos;
  } else if (hasHierarchicalFilter) {
    resolvedCCs = ['__NO_MATCH__'];
  }
  payload.CENTRO_COSTO = hasHierarchicalFilter ? resolvedCCs : (payload.CENTRO_COSTO || '');
  payload.PROVEEDOR = filterData.PROVEEDORES?.length ? filterData.PROVEEDORES : (payload.PROVEEDOR || '');
  payload.DIVISA = filterData.DIVISAS?.length ? filterData.DIVISAS : (payload.DIVISA || '');
  // COMPRADOR: si hay array de compradoras seleccionadas, pásalas; si no, dejar el campo individual
  payload.COMPRADOR = filterData.COMPRADORAS?.length ? filterData.COMPRADORAS : (payload.COMPRADOR || '');
  payload.EMPRESA = '';

  return payload;
};

export const useSolicitudesController = (apiUrl, options = {}) => {
  const { centrosCostoCatalogo = [] } = options;

  // ─── Estados generales
  const [loadingData, setLoadingData] = useState(true);
  const [loadingSpinner, setLoadingSpinner] = useState(false);
  const [spinnerMessage, setSpinnerMessage] = useState('');
  const [data, setData] = useState({ contenido: [], total: 0, hasMore: false });
  const [periodKpis, setPeriodKpis] = useState(null);
  const [excelData, setExcelData] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [modalSolicitudesOpen, setModalSolicitudesOpen] = useState(false);

  // ─── Datos de sesión
  const [nombreEmpresa, setNombreEmpresa] = useState(getSessionString('EmpresaNombre'));
  const [idEmpresa, setIdEmpresa] = useState(getSessionArray('EmpresaId'));
  const [loggedInUsername, setLoggedInUsername] = useState(getSessionString('loggedInUsername'));
  const [accesos, setAccesos] = useState(() => getSessionObject('Accesos') || {});

  const usuarioId = useMemo(() => Number(sessionStorage.getItem('UsuarioId') || 0), []);
  const idPerfil = useMemo(() => {
    const raw = sessionStorage.getItem('PerfilID') || sessionStorage.getItem('IdPerfil') || '0';
    try {
      const parsed = JSON.parse(raw);
      return Number(parsed) || 0;
    } catch {
      return Number(raw) || 0;
    }
  }, []);

  const empresaIdsKey = useMemo(() => {
    const arr = normalizeEmpresaArray(idEmpresa);
    return arr.length ? arr.join(',') : '';
  }, [idEmpresa]);

  const empresaIds = useMemo(() => {
    if (!empresaIdsKey) return [];
    return empresaIdsKey.split(',').map((n) => Number(n)).filter((x) => Number.isFinite(x) && x > 0);
  }, [empresaIdsKey]);

  const empresaIdPref = useMemo(() => (empresaIds.length > 1 ? 0 : (empresaIds[0] ?? 0)), [empresaIds]);

  const monthDates = useMemo(() => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const fmt = (d) => d.toLocaleDateString('en-CA');
    return { start: fmt(firstDay), end: fmt(lastDay) };
  }, []);

  const baseFilters = useMemo(() => {
    const initialEmpId = empresaIds.length === 1 ? empresaIds[0] : null;
    const initialEmpName = initialEmpId ? nombreEmpresa : '';

    return {
      FECHA_INICIO: monthDates.start,
      FECHA_FIN: monthDates.end,
      FOLIO: '',
      EMPRESA: initialEmpName,
      EMPRESA_IDS: initialEmpId ? [initialEmpId] : [],
      PROYECTO: '',
      PROYECTOS: [],
      DIVISION: '',
      DIVISIONES: [],
      PROVEEDOR: '',
      PROVEEDORES: [],
      CENTRO_COSTO: '',
      CENTRO_COSTOS: [],
      DEPARTAMENTO: '',
      DEPARTAMENTOS: [],
      COMPRADOR: '',
      COMPRADORAS: [],
      REQUISITOR: '',
      DIVISA: '',
      ESTATUS: '',
      DIVISAS: [],
    };
  }, [monthDates, empresaIds, nombreEmpresa]);

  const [tempFilterData, setTempFilterData] = useState(baseFilters);
  const [filterData, setFilterData] = useState(baseFilters);

  const prevResolvedCCsRef = useRef(EMPTY_CC_ARRAY);
  // Calcular automáticamente la resolución de Centros de Costo según el nivel (CC puntual > Grupo > Proyecto)
  const resolvedCCsForFilter = useMemo(() => {
    let result = EMPTY_CC_ARRAY;
    if ((filterData.CENTRO_COSTOS || []).length) {
      result = getCentroCostoFilterValues(filterData.CENTRO_COSTOS);
    } else {
      const hasDiv = (filterData.DIVISIONES || []).length > 0;
      const hasProy = (filterData.PROYECTOS || []).length > 0;
      if ((hasDiv || hasProy) && (centrosCostoCatalogo || []).length) {
        const divIds = (filterData.DIVISIONES || []).map(Number);
        const proyIds = (filterData.PROYECTOS || []).map(Number);
        const ccs = centrosCostoCatalogo.filter(c => {
          const matchDiv = !divIds.length || divIds.includes(Number(c.ID_DIVISION));
          const matchProy = !proyIds.length || proyIds.includes(Number(c.ID_PROYECTO));
          return matchDiv && matchProy;
        });
        const resolved = Array.from(new Set(ccs.flatMap(c => [
          String(c.NUMERO_CC || c.NUMERO || '').trim(),
          String(c.CENTRO_COSTO || c.NOMBRE || '').trim()
        ]).filter(Boolean)));
        result = resolved.length ? resolved : ['__NO_MATCH__'];
      }
    }

    // Estabilidad referencial: Si el contenido del arreglo no cambió respecto al anterior,
    // preservamos exactamente la misma referencia para evitar re-disparar fetchData.
    const prev = prevResolvedCCsRef.current;
    if (
      result === prev ||
      (result.length === 0 && prev.length === 0) ||
      (result.length === prev.length && result.every((val, idx) => val === prev[idx]))
    ) {
      return prev;
    }
    prevResolvedCCsRef.current = result;
    return result;
  }, [filterData.CENTRO_COSTOS, filterData.DIVISIONES, filterData.PROYECTOS, centrosCostoCatalogo]);

  const [tableParams, setTableParams] = useState({
    page: 1,
    pageSize: 25,
    globalQuery: '',
    sortField: null,
    sortDir: null,
  });
  const tableParamsRef = useRef(tableParams);
  tableParamsRef.current = tableParams;

  const selectedEmpresaIds = useMemo(() => {
    const selected = Array.isArray(filterData.EMPRESA_IDS)
      ? filterData.EMPRESA_IDS.map(Number).filter(id => Number.isInteger(id) && id > 0)
      : [];
    const scopedSelected = selected.filter(id => empresaIds.includes(id));
    if (scopedSelected.length) return scopedSelected;
    return empresaIds;
  }, [empresaIds, filterData.EMPRESA_IDS]);

  // Información formateada de los filtros activos para cuando no hay resultados
  const emptyStateData = useMemo(() => {
    const activeFiltersList = [];

    const formatFmt = (strDate) => {
      if (!strDate) return '';
      const parts = strDate.split('-');
      return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : strDate;
    };

    const dateRangeStr = (filterData.FECHA_INICIO && filterData.FECHA_FIN)
      ? `${formatFmt(filterData.FECHA_INICIO)} al ${formatFmt(filterData.FECHA_FIN)}`
      : '';

    if (filterData.FOLIO) activeFiltersList.push(`Folio/Requisición: "${filterData.FOLIO}"`);
    
    if (Array.isArray(filterData.DEPARTAMENTOS) && filterData.DEPARTAMENTOS.length > 0) {
      activeFiltersList.push(`Departamento: ${filterData.DEPARTAMENTOS.join(', ')}`);
    } else if (filterData.DEPARTAMENTO) {
      activeFiltersList.push(`Departamento: ${filterData.DEPARTAMENTO}`);
    }

    if (Array.isArray(filterData.CENTRO_COSTOS) && filterData.CENTRO_COSTOS.length > 0) {
      activeFiltersList.push(`Centro de Costo: ${filterData.CENTRO_COSTOS.join(', ')}`);
    } else if (filterData.CENTRO_COSTO) {
      activeFiltersList.push(`Centro de Costo: ${filterData.CENTRO_COSTO}`);
    }

    if (Array.isArray(filterData.PROVEEDORES) && filterData.PROVEEDORES.length > 0) {
      activeFiltersList.push(`Proveedor: ${filterData.PROVEEDORES.join(', ')}`);
    } else if (filterData.PROVEEDOR) {
      activeFiltersList.push(`Proveedor: ${filterData.PROVEEDOR}`);
    }

    if (Array.isArray(filterData.COMPRADORAS) && filterData.COMPRADORAS.length > 0) {
      activeFiltersList.push(`Compradora: ${filterData.COMPRADORAS.join(', ')}`);
    } else if (filterData.COMPRADOR) {
      activeFiltersList.push(`Compradora: ${filterData.COMPRADOR}`);
    }

    if (Array.isArray(filterData.DIVISAS) && filterData.DIVISAS.length > 0) {
      activeFiltersList.push(`Divisa: ${filterData.DIVISAS.join(', ')}`);
    } else if (filterData.DIVISA) {
      activeFiltersList.push(`Divisa: ${filterData.DIVISA}`);
    }

    if (tableParams.globalQuery) {
      activeFiltersList.push(`Búsqueda rápida: "${tableParams.globalQuery}"`);
    }

    return {
      dateRangeStr,
      activeFiltersList
    };
  }, [filterData, tableParams.globalQuery]);

  // ─── Escucha cambio de empresa
  useEffect(() => {
    const handleEmpresaChange = () => {
      setNombreEmpresa(getSessionString('EmpresaNombre'));
      setIdEmpresa(getSessionArray('EmpresaId'));
      setLoggedInUsername(getSessionString('loggedInUsername'));
      setAccesos(getSessionObject('Accesos') || {});

      // No forzamos fechas de hoy al cambiar empresa para permitir persistencia visual
      // pero si quisieras resetear, podrías hacerlo aquí como en Programadas
    };

    window.addEventListener('empresaCambiada', handleEmpresaChange);
    return () => window.removeEventListener('empresaCambiada', handleEmpresaChange);
  }, []);

  const ejecutarBusquedaManual = useCallback((filtrosPasados = null) => {
    setLoadingData(true);
    setFilterData(filtrosPasados || tempFilterData);
    setTableParams(prev => ({ ...prev, page: 1 }));
  }, [tempFilterData]);

  const aplicarFiltrosExternos = useCallback((nextFilters = tempFilterData, nextQuickSearch = '') => {
    const cleanSearch = String(nextQuickSearch || '').trim();
    const updatedParams = {
      ...tableParamsRef.current,
      globalQuery: cleanSearch,
      page: 1,
    };
    tableParamsRef.current = updatedParams;
    setLoadingData(true);
    setTableParams(updatedParams);
    setFilterData(nextFilters);
  }, [tempFilterData]);

  const updateVisibleFilter = useCallback((field, value, options = {}) => {
    const { apply = true, quickSearch = '' } = options;
    setTempFilterData(prev => {
      const updated = { ...prev, [field]: value };
      if (apply) {
        setLoadingData(true);
        setFilterData(updated);
        setTableParams(current => ({
          ...current,
          globalQuery: String(quickSearch || '').trim(),
          page: 1
        }));
      }
      return updated;
    });
  }, []);

  const abortRef = useRef(null);

  // ─── Cargar datos
  const fetchData = useCallback(async (customResolvedCCs = null) => {
    if (!apiUrl || !empresaIdsKey) return;

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoadingData(true);
    try {
      const { page, pageSize, globalQuery, sortField, sortDir } = tableParams;
      // Las fechas SIEMPRE se respetan (no se ignoran cuando hay filtros activos o búsqueda rápida)
      const serverFilterData = buildVisibleServerFilters(filterData, {
        ignoreDateFilters: false,
        resolvedCentroCostos: customResolvedCCs || (resolvedCCsForFilter.length ? resolvedCCsForFilter : null),
      });
      const combined = {
        ...buildFilterPayload(serverFilterData),
        globalQuery: globalQuery || '',
        sortField: sortField || null,
        sortDir: sortDir || null,
        page,
        pageSize
      };
      const respData = await getTablaSolicitudes({ 
        filterData: combined, 
        nombreEmpresa: selectedEmpresaIds.length === 1 && !nombreEmpresa?.toUpperCase().includes('TODA') && !nombreEmpresa?.toUpperCase().includes('ALL') ? nombreEmpresa : null, 
        idEmpresa: selectedEmpresaIds,
        usuarioId,
        idPerfil
      }, controller.signal);
      
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const contenido = (respData?.contenido || []).map(row => {
        let diasVenc = row.DIASRESTANTES;
        const fechaParaCalcular = row.VENCIMIENTO || row.FECHA_VENCIMIENTO;
        if (fechaParaCalcular) {
          const parts = fechaParaCalcular.split('T')[0].split('-');
          if (parts.length === 3) {
            const vDate = new Date(parts[0], parts[1] - 1, parts[2]);
            diasVenc = Math.ceil((vDate - today) / (1000 * 60 * 60 * 24));
          }
        }
        return {
          ...row,
          DIASRESTANTES: diasVenc,
        };
      });

      setData({
        ...respData,
        contenido
      });
    } catch (err) {
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
      console.error('Error fetching data:', err);
      handleOpenSweetAlert('Error al cargar datos de la tabla.', 'error');
    } finally {
      if (abortRef.current === controller) {
        setLoadingData(false);
        abortRef.current = null;
      }
    }
  }, [apiUrl, empresaIdsKey, selectedEmpresaIds, filterData, resolvedCCsForFilter, tableParams, nombreEmpresa, handleOpenSweetAlert, usuarioId, idPerfil]);

  // ─── Cargar KPIs globales de TODO el periodo (todas las páginas filtradas)
  const abortKpisRef = useRef(null);
  const fetchPeriodKpis = useCallback(async () => {
    if (!apiUrl || !empresaIdsKey) return;

    if (abortKpisRef.current) abortKpisRef.current.abort();
    const controller = new AbortController();
    abortKpisRef.current = controller;

    try {
      const serverFilterData = buildVisibleServerFilters(filterData, {
        ignoreDateFilters: false,
        resolvedCentroCostos: resolvedCCsForFilter.length ? resolvedCCsForFilter : null,
      });

      const batchSize = 1000;
      const fetchBatch = (p) => getTablaSolicitudes({
        filterData: {
          ...buildFilterPayload(serverFilterData),
          globalQuery: tableParamsRef.current.globalQuery || '',
          sortField: null,
          sortDir: null,
          page: p,
          pageSize: batchSize,
        },
        nombreEmpresa,
        idEmpresa: selectedEmpresaIds,
        usuarioId,
        idPerfil
      }, controller.signal);

      const firstResp = await fetchBatch(1);
      const firstContent = Array.isArray(firstResp?.contenido) ? firstResp.contenido : [];
      const totalFromServer = firstResp?.total || firstContent.length;

      let allRows = [...firstContent];

      if (totalFromServer > batchSize) {
        const totalPages = Math.min(Math.ceil(totalFromServer / batchSize), 10);
        const remainingPages = Array.from({ length: totalPages - 1 }, (_, i) => i + 2);
        const restResponses = await Promise.all(remainingPages.map(fetchBatch));
        allRows = [
          ...firstContent,
          ...restResponses.flatMap(r => Array.isArray(r?.contenido) ? r.contenido : []),
        ];
      }

      // Calcular acumulación global excluyendo órdenes canceladas (CANCELADO = 1)
      const divisasAcc = {};
      let sinSolicitudCount = 0;
      let sinSolicitudMonto = 0;
      let solParcialCount = 0;
      let solParcialMonto = 0;
      let solTotalCount = 0;
      let solTotalMonto = 0;
      let pagadasCount = 0;
      let totalActiveCount = 0;

      allRows.forEach((row) => {
        // Excluir órdenes canceladas del conteo y sumas de KPIs
        const isCancelada = Number(row.CANCELADO_ERP) === 1 ||
                            Number(row.CANCELADO) === 1 ||
                            String(row.ESTATUS || '').toUpperCase().includes('CANCELAD');
        if (isCancelada) return;

        const rawDivisa = String(row.DIVISA || row.MONEDA || 'MXN').trim().toUpperCase();
        const divisa = rawDivisa.includes('DOL') || rawDivisa.includes('USD') ? 'USD'
          : rawDivisa.includes('EUR') ? 'EUR' : 'MXN';

        if (!divisasAcc[divisa]) {
          divisasAcc[divisa] = { total: 0, solicitado: 0, restante: 0, count: 0 };
        }

        const totalOC = parseMoney(row.TOTAL ?? row.TOTAL_OC ?? 0) || 0;
        const solicitado = parseMoney(row.MONTOSOLICITADO ?? row.MONTO_SOLICITADO ?? 0) || 0;

        divisasAcc[divisa].total += totalOC;
        divisasAcc[divisa].solicitado = (divisasAcc[divisa].solicitado || 0) + solicitado;
        divisasAcc[divisa].restante = (divisasAcc[divisa].restante || 0) + (totalOC > solicitado ? totalOC - solicitado : 0);
        divisasAcc[divisa].count += 1;
        totalActiveCount += 1;

        const estatus = String(row.ESTATUS || '').trim().toUpperCase();
        if (!estatus || estatus.includes('SIN') || solicitado <= 0) {
          sinSolicitudCount += 1;
          sinSolicitudMonto += totalOC;
        } else if (estatus.includes('PARCIAL') || (solicitado > 0 && solicitado < totalOC)) {
          solParcialCount += 1;
          solParcialMonto += totalOC;
        } else if (estatus.includes('PAGAD') || estatus.includes('PAGO')) {
          pagadasCount += 1;
        } else {
          solTotalCount += 1;
          solTotalMonto += totalOC;
        }
      });

      const TASA_USD_MXN = 18.50;
      const TASA_EUR_MXN = 20.00;
      const totalEstimadoMXN = (divisasAcc['MXN']?.total || 0) + 
        ((divisasAcc['USD']?.total || 0) * TASA_USD_MXN) + 
        ((divisasAcc['EUR']?.total || 0) * TASA_EUR_MXN);

      setPeriodKpis({
        totalCount: totalActiveCount,
        divisas: divisasAcc,
        totalEstimadoMXN,
        funnel: {
          sinSolicitud: { count: sinSolicitudCount, monto: sinSolicitudMonto },
          solParcial: { count: solParcialCount, monto: solParcialMonto },
          solTotal: { count: solTotalCount, monto: solTotalMonto },
          pagadas: { count: pagadasCount },
          enTramiteCount: solParcialCount + solTotalCount,
        }
      });
    } catch (e) {
      if (e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED') return;
      console.warn('Error calculando KPIs globales del periodo:', e);
    }
  }, [apiUrl, empresaIdsKey, selectedEmpresaIds, filterData, resolvedCCsForFilter, nombreEmpresa, usuarioId, idPerfil]);

  // Recalcular KPIs globales en segundo plano después de que la tabla principal termine de cargar
  useEffect(() => {
    const kpiTimer = setTimeout(() => {
      fetchPeriodKpis();
    }, 300);
    return () => {
      clearTimeout(kpiTimer);
      if (abortKpisRef.current) abortKpisRef.current.abort();
    };
  }, [fetchPeriodKpis]);

  const handleExportFetchAll = useCallback(async (exportOptions = {}) => {
    if (!apiUrl || !empresaIdsKey) return data;

    const batchSize = 500; // Lotes grandes para menos round-trips
    const exportScope = exportOptions?.scope || 'filters';
    const cleanDateFilters = {
      FECHA_INICIO: exportOptions?.fromDate || null,
      FECHA_FIN: exportOptions?.toDate || null,
    };
    const baseExportFilters = exportScope === 'filters'
      ? filterData
      : exportScope === 'dateRange'
        ? cleanDateFilters
        : {};
    const shouldIgnoreDates = exportScope === 'all';
    const serverFilterData = buildVisibleServerFilters(baseExportFilters, {
      ignoreDateFilters: shouldIgnoreDates,
    });
    const exportGlobalQuery = exportScope === 'filters'
      ? (tableParamsRef.current.globalQuery || '')
      : '';

    const fetchPage = (page) => getTablaSolicitudes({
      filterData: {
        ...buildFilterPayload(serverFilterData),
        globalQuery: exportGlobalQuery,
        sortField: tableParamsRef.current.sortField || null,
        sortDir: tableParamsRef.current.sortDir || null,
        page,
        pageSize: batchSize,
      },
      nombreEmpresa,
      idEmpresa: selectedEmpresaIds,
      usuarioId,
      idPerfil
    });

    try {
      // ── 1. Página 1: obtener datos + total real ──
      const firstResp = await fetchPage(1);
      const firstContent = Array.isArray(firstResp?.contenido) ? firstResp.contenido : [];
      const totalFromServer = firstResp?.total || 0;

      let allData = [];

      // Si todo cabe en la primera página, terminamos ya
      if (firstContent.length < batchSize) {
        allData = [...firstContent];
      }
      // ── 2. Si el servidor nos dio un TOTAL real mayor al batch, hacemos peticiones en PARALELO ──
      else if (totalFromServer > batchSize) {
        const totalPages = Math.min(Math.ceil(totalFromServer / batchSize), 10);
        const remainingPages = Array.from({ length: totalPages - 1 }, (_, i) => i + 2);

        const restResponses = await Promise.all(remainingPages.map(fetchPage));

        allData = [
          ...firstContent,
          ...restResponses.flatMap(r => Array.isArray(r?.contenido) ? r.contenido : []),
        ];
      }
      // ── 3. Si el servidor NO dio el TOTAL (pero el batch llegó lleno), hacemos peticiones SECUENCIALES ──
      else {
        allData = [...firstContent];
        let currentPage = 2;
        let hasMore = true;

        while (hasMore) {
          const nextResp = await fetchPage(currentPage);
          const nextContent = Array.isArray(nextResp?.contenido) ? nextResp.contenido : [];
          if (nextContent.length > 0) {
            allData = [...allData, ...nextContent];
            hasMore = nextContent.length === batchSize;
            currentPage++;
          } else {
            hasMore = false;
          }
          if (currentPage > 40) break; // tope 10,000 registros
        }
      }

      // Recalcular DIASRESTANTES para todos los registros exportados
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const finalContent = allData.map(row => {
        let diasVenc = null;
        if (row.VENCIMIENTO) {
          const parts = row.VENCIMIENTO.split('-');
          if (parts.length === 3) {
            const vDate = new Date(parts[0], parts[1] - 1, parts[2]);
            diasVenc = Math.ceil((vDate - today) / (1000 * 60 * 60 * 24));
          }
        }
        return { ...row, DIASRESTANTES: diasVenc };
      });

      return { contenido: finalContent, total: finalContent.length };
    } catch (err) {
      console.error('Error fetching all data for export:', err);
      return data; // Fallback to current page
    }
  }, [apiUrl, empresaIdsKey, selectedEmpresaIds, filterData, nombreEmpresa, data]);

  useEffect(() => {
    fetchData();
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, [fetchData]);

  // ─── Lógica para Modal
  const handleOpenSolicitudes = useCallback(async (row) => {
    if (Number(row?.CANCELADO_ERP) === 1) {
      handleOpenSweetAlert('Esta orden está cancelada desde el ERP y no es posible generar una solicitud.', 'error');
      return;
    }

    const totalRaw = row?.TOTAL ?? row?.TOTALOC ?? row?.TOTAL_OC;
    const total = parseMoney(totalRaw);

    if (!Number.isFinite(total) || total <= 0) {
      handleOpenSweetAlert('No es posible generar la solicitud porque el TOTAL de la orden es 0 (o inválido).', 'warning');
      return;
    }

    const proveedor = row?.PROVEEDOR ?? row?.NOMBRE_PROVEEDOR ?? row?.PROVEEDOR_NOMBRE;
    if (!proveedor || String(proveedor).trim() === '') {
      handleOpenSweetAlert('No es posible generar la solicitud porque la orden no tiene proveedor asignado.', 'warning');
      return;
    }

    try {
      setSpinnerMessage('Verificando nivel de autorización...');
      setLoadingSpinner(true);

      const nivelPayload = {
        empresa: row.EMPRESA,
        centroCosto: row.CENTROCOSTOS || row.NOMBRE_CC || row.CENTRO_COSTO || row.CENTROCOSTO,
        departamento: row.DEPARTAMENTO,
        divisa: row.DIVISA,
        session: loggedInUsername,
        folio: row.FOLIO,
      };
      const response = await verificarNivelAut(nivelPayload);
      setLoadingSpinner(false);

      if (response.existeNivelAutorizacion) {
        setSelectedRow(row);
        setModalSolicitudesOpen(true);
      } else {
        await handleOpenSweetAlert(response.mensaje || 'No cuentas con nivel de autorización para esta solicitud.', 'warning');
      }
    } catch (err) {
      setLoadingSpinner(false);
      console.error('Error al verificar nivel de autorización:', err);
      await handleOpenSweetAlert('Error al verificar el nivel de autorización.', 'error');
    }
  }, [loggedInUsername, handleOpenSweetAlert]);




  // ─── Lógica para Modal

  const handleCloseSolicitudes = useCallback(() => {
    setModalSolicitudesOpen(false);
    setSelectedRow(null);
  }, []);

  const handleSolicitudGuardada = useCallback(() => {
    fetchData();
  }, [fetchData]);

  // ─── Importación
  // Firma: handleImport(modo, { fechaInicio, fechaFin, folioOC, soloEntradasPagos })
  // El SyncDialog llama esta función con sus propios valores de fecha/folio.
  const handleImport = useCallback(async (modo = 'rango', syncPayload = null) => {
    const hoy = getCurrentDate();

    // Si viene payload del SyncDialog, usarlo; si no, leer de los filtros (compatibilidad)
    let fechaInicio = syncPayload?.fechaInicio ?? null;
    let fechaFin    = syncPayload?.fechaFin    ?? null;
    let folioOC     = syncPayload?.folioOC     ?? null;

    if (!syncPayload) {
      // Modo legacy: toma valores del filtro
      const { FECHA_INICIO, FECHA_FIN, FOLIO, FOLIO_ORDENCOMPRA } = tempFilterData;
      folioOC = FOLIO_ORDENCOMPRA || FOLIO || '';
      if (modo === 'hoy')   { fechaInicio = hoy; fechaFin = hoy; }
      if (modo === 'rango') { fechaInicio = FECHA_INICIO; fechaFin = FECHA_FIN; }
    }

    // Cuando es 'hoy' y no vino del dialog, forzar fecha
    if (modo === 'hoy' && !fechaInicio) { fechaInicio = hoy; fechaFin = hoy; }

    const usarSpinnerGlobal = !syncPayload;
    if (usarSpinnerGlobal) setLoadingSpinner(true);
    try {
      const response = await sincronizarERP({
        modo,
        fechaInicio: fechaInicio || null,
        fechaFin:    fechaFin    || null,
        folioOC:     folioOC     || null,
        empresas:    (selectedEmpresaIds && selectedEmpresaIds.length > 0) ? selectedEmpresaIds : empresaIds,
        soloEntradasPagos: syncPayload?.soloEntradasPagos || false,
      });

      const ok = response?.ok === true;

      if (ok) {
        let desc = '';
        if (modo === 'hoy')   desc = `de hoy (${hoy}).`;
        if (modo === 'rango') desc = `del rango ${fechaInicio} a ${fechaFin}.`;
        if (modo === 'folio') desc = `para el folio ${folioOC}.`;
        await registrarBitacora('S/V', `El usuario ${loggedInUsername} ha realizado la sincronización ERP (${modo}) ${desc}`, loggedInUsername, 'Sincronización ERP', 'Datos ERP');
      }

      if (!syncPayload) {
        // En flujo legacy mostrar SweetAlert y esperar aceptación antes de recargar
        await handleOpenSweetAlert(
          response?.mensaje || (ok ? 'Sincronización completada.' : 'Ocurrió un problema al sincronizar.'),
          ok ? 'success' : 'error'
        );
      }

      if (ok) {
        fetchData();
      }

      return response; // SyncDialog lee ok / sinResultados de aquí
    } catch (err) {
      const msg = err.response?.data?.mensaje || err.response?.data?.error || err.message || 'Error de servidor al sincronizar ERP.';
      if (!syncPayload) {
        await handleOpenSweetAlert(msg, 'error');
      }
      throw new Error(msg); // SyncDialog captura esto para mostrar el mensaje específico inline
    } finally {
      if (usarSpinnerGlobal) setLoadingSpinner(false);
    }
  }, [tempFilterData, empresaIds, loggedInUsername, fetchData, handleOpenSweetAlert]);

  const handleTableStateChange = useCallback((newState) => {
    const newPage = (newState.page ?? 0) + 1;
    const newPageSize = newState.rowsPerPage || 25;
    const newGQ = newState.globalQuery !== undefined ? newState.globalQuery : (tableParamsRef.current.globalQuery || '');
    const newSF = newState.sortConfig?.field || null;
    const newSD = newState.sortConfig?.direction || null;

    setTableParams(prev => {
      const resetPage = prev.globalQuery !== newGQ ||
                        prev.sortField !== newSF ||
                        prev.sortDir !== newSD ||
                        prev.pageSize !== newPageSize;

      const nextParams = {
        page: resetPage ? 1 : newPage,
        pageSize: newPageSize,
        globalQuery: newGQ,
        sortField: newSF,
        sortDir: newSD,
      };

      if (
        prev.page === nextParams.page &&
        prev.pageSize === nextParams.pageSize &&
        prev.globalQuery === nextParams.globalQuery &&
        prev.sortField === nextParams.sortField &&
        prev.sortDir === nextParams.sortDir
      ) {
        return prev;
      }

      setLoadingData(true);
      tableParamsRef.current = nextParams;
      return nextParams;
    });
  }, []);

  return {
    // state variables
    data,
    periodKpis,
    loadingData,
    loadingSpinner,
    spinnerMessage,
    excelData,
    selectedRow,
    modalSolicitudesOpen,
    tempFilterData,
    filterData,
    baseFilters,
    usuarioId,
    idPerfil,
    empresaIdPref,
    empresaIds,
    selectedEmpresaIds,
    nombreEmpresa,
    emptyStateData,

    // setters that are safe to expose
    setTempFilterData,
    setFilterData,
    setTableParams,
    setExcelData,
    setLoadingSpinner,

    // actions
    ejecutarBusquedaManual,
    aplicarFiltrosExternos,
    updateVisibleFilter,
    handleImport,
    handleOpenSolicitudes,
    handleCloseSolicitudes,
    handleSolicitudGuardada,
    handleTableStateChange,
    handleExportFetchAll,
    fetchData,
  };
};
